import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Heading } from "react-aria-components";

import { isAdminRole, useViewer } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { addToast } from "@/components/base/toast";
import { useTeamsQueryOptions } from "@/components/tournaments/context";
import { useTRPC } from "@/integrations/trpc/react";

export type TdSwapPlayersFormProps = {
  tournamentDivisionTeamId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TdSwapPlayersForm({
  tournamentDivisionTeamId,
  ...props
}: TdSwapPlayersFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const teamQueryOptions = useTeamsQueryOptions();
  const viewer = useViewer();
  const isAdmin = isAdminRole(viewer?.role);

  const { data: teamInfo } = useQuery({
    ...trpc.tournaments.getTeamForSwap.queryOptions({
      tournamentDivisionTeamId,
    }),
    enabled: props.isOpen,
  });

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.tournaments.tdSwapPlayers.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamQueryOptions);
      queryClient.invalidateQueries({
        queryKey: trpc.tournaments.getInvalidRosters.queryKey(),
      });

      addToast({
        variant: "success",
        title: "Players swapped",
        description: "The team roster has been updated.",
      });

      props.onOpenChange(false);
    },
  });

  // Seed the form from the team, and keep this reference stable.
  //
  // `useForm` calls `form.update(options)` on every render, and `FormApi.update`
  // re-applies `defaultValues` whenever they look new and the form is untouched.
  // An inline object literal is a new reference every render, so the form's
  // values were being reset to the empty default continuously — which is why
  // the roster pickers never appeared. Memoising on `teamInfo` means the
  // defaults change exactly once, when the team loads.
  //
  // One slot per roster spot, not one per player already on the team: a short
  // team — demo fill data, a removed player — would otherwise render too few
  // inputs to ever put anyone back.
  const defaultValues = useMemo(
    () => ({
      players: Array.from(
        { length: teamInfo?.teamSize ?? 0 },
        (_, i): number | null => teamInfo?.playerProfileIds[i] ?? null,
      ),
      acknowledgeInvalid: false,
      suppressNotifications: false,
    }),
    [teamInfo],
  );

  const form = useAppForm({
    defaultValues,
    onSubmit: ({ value }) => {
      mutate({
        tournamentDivisionTeamId,
        players: value.players.filter((p): p is number => p !== null),
        acknowledgeInvalid: value.acknowledgeInvalid,
        suppressNotifications: value.suppressNotifications,
      });
    },
  });

  // Select the stored array itself — a selector that builds a new array every
  // call makes `useStore` see a change on every render and thrash.
  const players = useStore(form.store, (state) => state.values.players);
  const acknowledged = useStore(
    form.store,
    (state) => state.values.acknowledgeInvalid,
  );

  // Has the admin actually changed anything? A roster can arrive already
  // invalid — demo fill data, a lapsed membership — and warning about a state
  // the admin didn't create just buries the controls they came here to use.
  //
  // `isDirty` is the form's own "values differ from defaultValues", so it is
  // false while the form is still being seeded from `teamInfo` and true only
  // once a picker has actually been changed.
  const isDirty = useStore(form.store, (state) => state.isDirty);

  const selectedPlayers = useMemo(
    () => players.filter((p): p is number => p !== null),
    [players],
  );

  // What would be wrong with this roster if it were saved? Only asked once
  // there is an edit to judge. The server decides `canOverride` — false for
  // non-admins, and false when there is nothing to acknowledge — so the
  // checkbox is never offered pointlessly.
  const { data: preview } = useQuery({
    ...trpc.tournaments.previewRosterValidation.queryOptions({
      tournamentDivisionTeamId,
      players: selectedPlayers,
    }),
    enabled: props.isOpen && isDirty,
  });

  const violations = isDirty ? (preview?.violations ?? []) : [];
  const hasBlocking = isDirty && (preview?.hasBlocking ?? false);
  const canOverride = isDirty && (preview?.canOverride ?? false);

  return (
    <Modal {...props}>
      <form
        className="flex flex-col space-y-4 p-3 md:p-6"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Heading className={title({ size: "sm" })} slot="title">
          Swap Partners
        </Heading>

        {failureReason && (
          <form.AppForm>
            <form.Alert
              title="Unable to swap players"
              description={failureReason.message}
            />
          </form.AppForm>
        )}

        <p className="text-sm text-gray-700">
          Replace players on this team. Search for players by name.
        </p>

        <form.Subscribe
          selector={(state) => state.values.players}
          children={(players) => (
            <form.AppField
              name="players"
              mode="array"
              children={(field) =>
                field.state.value.map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size team players array
                  <form.AppField key={i} name={`players[${i}]`}>
                    {(subField) => (
                      <subField.ProfilePicker
                        label={`Player ${i + 1}`}
                        field={subField}
                        gender={teamInfo?.gender}
                        selectedProfileIds={players.filter(
                          (p): p is number => p !== null,
                        )}
                      />
                    )}
                  </form.AppField>
                ))
              }
            />
          )}
        />

        {violations.length > 0 && (
          <form.AppForm>
            <form.Alert
              color={hasBlocking ? "error" : "warning"}
              title={
                hasBlocking
                  ? "This roster can't be saved"
                  : "This roster would be invalid"
              }
              // `Alert` renders its description inside a <p>, so this has to
              // be phrasing content — a <ul> here is invalid HTML and trips a
              // hydration error.
              description={violations.map((violation) => (
                <span
                  key={`${violation.rule}-${violation.profileIds.join()}`}
                  className="block"
                >
                  {violation.message}
                </span>
              ))}
            />
          </form.AppForm>
        )}

        {canOverride && (
          <form.AppField name="acknowledgeInvalid">
            {(field) => (
              <field.Checkbox
                field={field}
                label="I understand this roster will be invalid and I'll fix it before the tournament"
              />
            )}
          </form.AppField>
        )}

        {isAdmin && (
          <form.AppField name="suppressNotifications">
            {(field) => (
              <field.Checkbox
                field={field}
                label="Don't notify players about this change"
              />
            )}
          </form.AppField>
        )}

        <form.AppForm>
          <form.Footer>
            <Button
              onPress={() => props.onOpenChange(false)}
              isDisabled={isPending}
            >
              Cancel
            </Button>
            <form.SubmitButton
              isDisabled={
                isPending ||
                hasBlocking ||
                (violations.length > 0 && !acknowledged)
              }
            >
              {isPending ? "Swapping..." : "Swap Partners"}
            </form.SubmitButton>
          </form.Footer>
        </form.AppForm>
      </form>
    </Modal>
  );
}
